import { Controller } from "@hotwired/stimulus"

// EPIC-023 §4: a tap-triggered toggletip (NOT hover) — the indicator itself is the
// trigger (no "i" badge). Tap shows a short balloon hint announced via the bubble's
// role="status"/aria-live; tap-again / Esc / tap-away dismisses; only one open at a
// time (opening one closes the others). No essential info lives here — it's progressive
// enhancement over the always-visible icon + master-status.
//
// `float` mode (EPIC-029 fix): when the trigger lives inside a clipping container
// (the slot popup's overflow:hidden card + the sheet's overflow-y-auto), CSS-absolute
// positioning gets the balloon clipped. With data-toggletip-float-value="true" the
// balloon is positioned `fixed` against the trigger's viewport rect (clamped to the
// viewport, flipped above when there's no room below) so it can never be clipped.
export default class extends Controller {
  static targets = ["bubble"]
  static values = { float: Boolean }

  connect() {
    this.onDocPointer = this.onDocPointer.bind(this)
    this.onKey = this.onKey.bind(this)
    this.onCloseOthers = this.onCloseOthers.bind(this)
    this.reposition = this.reposition.bind(this)
    document.addEventListener("toggletip:open", this.onCloseOthers)
  }

  disconnect() {
    document.removeEventListener("toggletip:open", this.onCloseOthers)
    this.teardown()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.triggerElement = event.currentTarget
    this.open ? this.close() : this.show()
  }

  preserveOpen(event) {
    if (!this.open) return

    const attribute = event.detail.attributeName
    const triggerState = event.target === this.triggerElement && attribute === "aria-expanded"
    const bubbleState = this.hasBubbleTarget && event.target === this.bubbleTarget &&
      [ "hidden", "style" ].includes(attribute)
    if (triggerState || bubbleState) event.preventDefault()
  }

  show() {
    if (!this.hasBubbleTarget) return

    document.dispatchEvent(new CustomEvent("toggletip:open", { detail: { source: this } }))
    this.bubbleTarget.hidden = false
    this.triggerElement?.setAttribute("aria-expanded", "true")
    if (this.floatValue) {
      this.reposition()
      window.addEventListener("scroll", this.reposition, true)
      window.addEventListener("resize", this.reposition)
    }
    // listen on the next tick so the opening tap doesn't immediately close it
    window.setTimeout(() => {
      document.addEventListener("pointerdown", this.onDocPointer)
      document.addEventListener("keydown", this.onKey)
    }, 0)
    this.open = true
  }

  close() {
    if (!this.hasBubbleTarget) return

    this.bubbleTarget.hidden = true
    this.triggerElement?.setAttribute("aria-expanded", "false")
    if (this.floatValue) {
      window.removeEventListener("scroll", this.reposition, true)
      window.removeEventListener("resize", this.reposition)
      // clear the inline position so the next open recomputes from scratch
      const b = this.bubbleTarget
      b.style.position = b.style.top = b.style.left = b.style.right = b.style.bottom = ""
    }
    this.teardown()
    this.open = false
  }

  // Position the balloon `fixed` against the trigger's viewport rect: below by default,
  // flipped above when it would overflow the bottom; clamped horizontally; aligned to the
  // trigger's start edge (or its end edge for data-toggletip-align="end").
  reposition() {
    const r = this.element.getBoundingClientRect()
    const b = this.bubbleTarget
    const margin = 8
    b.style.position = "fixed"
    b.style.right = "auto"
    b.style.bottom = "auto"
    const bw = b.offsetWidth
    const bh = b.offsetHeight
    let left = this.element.dataset.toggletipAlign === "end" ? r.right - bw : r.left
    left = Math.max(margin, Math.min(left, window.innerWidth - bw - margin))
    let top = r.bottom + 6
    if (top + bh + margin > window.innerHeight) top = r.top - bh - 6
    b.style.left = `${Math.round(left)}px`
    b.style.top = `${Math.round(Math.max(margin, top))}px`
  }

  teardown() {
    document.removeEventListener("pointerdown", this.onDocPointer)
    document.removeEventListener("keydown", this.onKey)
  }

  onDocPointer(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  onKey(event) {
    if (event.key === "Escape") this.close()
  }

  onCloseOthers(event) {
    if (event.detail.source !== this && this.open) this.close()
  }
}
